import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixInnovationWidgetV1Message from './innovation-widget-v1-message-messages.http.js';

export interface Message {
  /**
   * Message ID.
   * @maxLength 40
   * @readonly
   */
  _id?: string | null;
  /**
   * Date and time the message was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * __Required.__
   *
   * - `"UOU"`: Visitor sent message.
   * - `"ASSISTANT"`: AI assistant.
   * - `"SYSTEM"`: System notification.
   */
  sender?: SenderWithLiterals;
  /**
   * Conversation ID.
   * @format GUID
   * @readonly
   */
  conversationId?: string | null;
  /**
   * ID of the message that this message is answering.
   * @maxLength 40
   */
  answerTo?: string | null;
  /**
   * __Required.__
   * Message type.
   *
   */
  messageType?: MessageTypeWithLiterals;
  /** Message body. */
  body?: Body;
  /**
   * Message Id in the Inbox
   * @maxLength 40
   * @readonly
   */
  inboxId?: string | null;
  /**
   * Options site visitor can choose from.
   * @maxSize 10
   * @maxLength 500
   */
  visitorOptions?: string[];
  /**
   * List of actions that ai assistant executed on the site visitor's behalf.
   * @maxSize 10
   * @maxLength 500
   */
  assistantActions?: string[];
  /**
   * List of detailed options the visitor can choose from.
   * @readonly
   * @maxSize 50
   */
  visitorOptionsFields?: Record<string, any>[] | null;
  /**
   * Exact status message returned by ai site chat engine,
   * @maxLength 50
   * @readonly
   */
  engineStatus?: string | null;
}

export enum Sender {
  /** AI assistant. */
  ASSISTANT = 'ASSISTANT',
  /** Wix user. For example, a site admin. */
  USER = 'USER',
  /** Message was sent automatically due to widget settings. For example, a contact form. */
  SYSTEM = 'SYSTEM',
  /** Site visitor. */
  SITE_VISITOR = 'SITE_VISITOR',
}

/** @enumType */
export type SenderWithLiterals =
  | Sender
  | 'ASSISTANT'
  | 'USER'
  | 'SYSTEM'
  | 'SITE_VISITOR';

export enum MessageType {
  /** Question. */
  QUESTION = 'QUESTION',
  /** Answer. */
  ANSWER = 'ANSWER',
  /** Intro message, as defined in the widget settings. */
  INTRO = 'INTRO',
  /** Legal disclaimer, as defined in the widget settings. */
  LEGAL = 'LEGAL',
  /** Contact form. */
  CONTACT_FORM = 'CONTACT_FORM',
  /** Message confirms that a contact form was successfully submitted. */
  CONTACT_FORM_SUBMITTED = 'CONTACT_FORM_SUBMITTED',
  /** Notification for the site owner. */
  SITE_OWNER_NOTIFICATION = 'SITE_OWNER_NOTIFICATION',
  /** Message informing the site visitor that the AI assistant is offline, as defined in the widget settings. */
  OFFLINE = 'OFFLINE',
  /** Message that informs the Wix user that they have reached the maximum number of messages on their current plan. */
  OUT_OF_QUOTA = 'OUT_OF_QUOTA',
  /** User answer. */
  USER_ANSWER = 'USER_ANSWER',
  /** Site visitor site Navigation information. */
  SITE_NAVIGATION = 'SITE_NAVIGATION',
  /** Site visitor site Navigation information answer. */
  NAVIGATION_ANSWER = 'NAVIGATION_ANSWER',
  /** AI Site Chat ignored the question. */
  IGNORED_ANSWER = 'IGNORED_ANSWER',
  /** Site visitor selected an option from  Message.visitor_options. */
  VISITOR_OPTION_SELECTED = 'VISITOR_OPTION_SELECTED',
  /** Site visitor added a product to the cart. */
  ADD_TO_CART = 'ADD_TO_CART',
  /** Site visitor booked a service. */
  BOOK_SERVICE = 'BOOK_SERVICE',
  /** AI assistant generated a question for the user to try. */
  GENERATED_QUESTION = 'GENERATED_QUESTION',
  /** Suggested question, as defined in the widget settings. */
  SUGGESTED_QUESTION = 'SUGGESTED_QUESTION',
  /** Wix user question for testing and training. */
  USER_QUESTION = 'USER_QUESTION',
  /** Wix assistant answer for testing and training. */
  USER_QUESTION_ANSWER = 'USER_QUESTION_ANSWER',
  /** Media uploaded by Wix user for visitor. */
  WIX_USER_MEDIA = 'WIX_USER_MEDIA',
  /** Media uploaded by visitor for Wix user. */
  MEDIA = 'MEDIA',
  /** Card template sent by the Wix User. */
  WIX_USER_CARD = 'WIX_USER_CARD',
  /** Simple template sent by the Wix User. */
  WIX_USER_MINIMAL_BODY = 'WIX_USER_MINIMAL_BODY',
  /** Form sent by the Wix User. */
  WIX_USER_FORM = 'WIX_USER_FORM',
  /** System message from comm channels. */
  WIX_SYSTEM = 'WIX_SYSTEM',
}

/** @enumType */
export type MessageTypeWithLiterals =
  | MessageType
  | 'QUESTION'
  | 'ANSWER'
  | 'INTRO'
  | 'LEGAL'
  | 'CONTACT_FORM'
  | 'CONTACT_FORM_SUBMITTED'
  | 'SITE_OWNER_NOTIFICATION'
  | 'OFFLINE'
  | 'OUT_OF_QUOTA'
  | 'USER_ANSWER'
  | 'SITE_NAVIGATION'
  | 'NAVIGATION_ANSWER'
  | 'IGNORED_ANSWER'
  | 'VISITOR_OPTION_SELECTED'
  | 'ADD_TO_CART'
  | 'BOOK_SERVICE'
  | 'GENERATED_QUESTION'
  | 'SUGGESTED_QUESTION'
  | 'USER_QUESTION'
  | 'USER_QUESTION_ANSWER'
  | 'WIX_USER_MEDIA'
  | 'MEDIA'
  | 'WIX_USER_CARD'
  | 'WIX_USER_MINIMAL_BODY'
  | 'WIX_USER_FORM'
  | 'WIX_SYSTEM';

export interface Body {
  /**
   * Main text of the message.
   * @maxLength 4096
   */
  mainText?: string | null;
  /**
   * Footer text of the message.
   * @maxLength 4096
   */
  footerText?: string | null;
  /**
   * Additional data for the message.
   * @maxSize 30
   */
  additionalData?: Record<string, any>[] | null;
}

export enum ChatHandler {
  /** Unknown handler. */
  UNKNOWN_HANDLER = 'UNKNOWN_HANDLER',
  /** AI assistant. */
  AI = 'AI',
  /** Wix user, such as a site owner. */
  WIX_USER = 'WIX_USER',
}

/** @enumType */
export type ChatHandlerWithLiterals =
  | ChatHandler
  | 'UNKNOWN_HANDLER'
  | 'AI'
  | 'WIX_USER';

export interface BulkCreateMessagesRequest {
  /**
   * Messages to be created.
   * @minSize 1
   * @maxSize 25
   */
  messages?: Message[];
}

export interface BulkCreateMessagesResponse {
  /** Created messages. */
  messages?: Message[];
}

export interface CreateMessageStreamRequest {
  /** Message to create and stream a response for. */
  message: Message;
}

export interface CreateMessageStreamResponse
  extends CreateMessageStreamResponseResponseOneOf {
  /** The persisted request message (with server-assigned id, timestamps, conversation id). */
  question?: Message;
  /** The latest cumulative state of the assistant's answer. */
  answer?: Message;
  /**
   * Part of the assistant's answer, not necessarily a complete message (e.g. for streaming tokens as they are generated).
   * @maxLength 5000
   */
  message?: string;
}

/** @oneof */
export interface CreateMessageStreamResponseResponseOneOf {
  /** The persisted request message (with server-assigned id, timestamps, conversation id). */
  question?: Message;
  /** The latest cumulative state of the assistant's answer. */
  answer?: Message;
  /**
   * Part of the assistant's answer, not necessarily a complete message (e.g. for streaming tokens as they are generated).
   * @maxLength 5000
   */
  message?: string;
}

export interface ListMessagesRequest {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListMessagesResponse {
  /** Retrieved messages. */
  messages?: Message[];
  /** Paging metadata. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface MediaAttachmentUploadUrlRequest {
  /**
   * File type e.g. "image/png".
   * @maxLength 100
   */
  mimeType: string;
  /**
   * File name
   * @maxLength 100
   */
  fileName?: string | null;
  /**
   * @maxLength 100
   * @deprecated
   * @targetRemovalDate 2026-06-25
   */
  parentFolderId?: string | null;
  /**
   * @maxLength 100
   * @deprecated
   * @targetRemovalDate 2026-06-25
   */
  filePath?: string | null;
}

export interface MediaAttachmentUploadUrlResponse {
  /** @format WEB_URL */
  uploadUrl?: string;
}

export interface BulkGetByInboxMessagesRequest {
  /**
   * conversationId of the conversation in inbox.
   *
   * Can be found in [Message.conversationId](https://dev.wix.com/docs/api-reference/crm/communication/inbox/messages/message-object)
   * @format GUID
   */
  inboxConversationId: string | null;
  /**
   * List of message sequences in the inbox conversation to retrieve messages for.
   *
   * Can be found in [Message.sequence](https://dev.wix.com/docs/api-reference/crm/communication/inbox/messages/message-object)
   * @maxLength 20
   * @minSize 1
   * @maxSize 100
   */
  inboxMessageSequences?: string[] | null;
}

export interface BulkGetByInboxMessagesResponse {
  results?: BulkGetByInboxMessagesResult[];
  /**
   * If message was not found, still counts as success.
   * Failure to retrieve a message will result in a failure count.
   */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkGetByInboxMessagesResult {
  itemMetadata?: ItemMetadata;
  /** Message object if found, otherwise empty. */
  message?: Message;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface ScheduleCreateInsightsRequest {
  /** @format GUID */
  metaSiteId?: string;
  scheduleFor?: Date | null;
}

export interface ScheduleCreateInsightsResponse {
  scheduledFor?: Date | null;
}

export interface CancelCreateInsightsRequest {
  /** @format GUID */
  metaSiteId?: string;
}

export interface CancelCreateInsightsResponse {}

export interface SetupRequest {}

export interface SetupResponse {}

export interface TeardownRequest {}

export interface TeardownResponse {}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
  /**
   * Symphony — a siteless site representing a project within Symphony (the evolution of Orion).
   * Holds project data, conversations, assets, and manages collaborators / shared team members for the project.
   */
  SYMPHONY = 'SYMPHONY',
  /** Nautilus platform app. */
  NAUTILUS_APPS = 'NAUTILUS_APPS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS'
  | 'SYMPHONY'
  | 'NAUTILUS_APPS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface CommunicationsChannelsMessageReceived {
  /** @maxLength 100 */
  externalMessageId?: string | null;
  inboxSequence?: string | null;
  /** @maxLength 100 */
  channelId?: string;
}

export interface VisitorMappedToContact {
  /** visitor id */
  visitorId?: string;
  /** contact id */
  contactId?: string;
  /** previous visitor id */
  previousContactId?: string | null;
  /** mapping valid starting on */
  validFromDate?: Date | null;
  /** New/Existing contact */
  allocationResultType?: AllocationResultTypeWithLiterals;
}

export enum AllocationResultType {
  NEW_CONTACT = 'NEW_CONTACT',
  EXISTING_CONTACT = 'EXISTING_CONTACT',
}

/** @enumType */
export type AllocationResultTypeWithLiterals =
  | AllocationResultType
  | 'NEW_CONTACT'
  | 'EXISTING_CONTACT';

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Bulk creates messages.
 *
 * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is creating the messages.
 * @public
 * @requiredField options.messages.messageType
 * @permissionId INNOVATION_LAB.CHAT_WIDGET_MESSAGE_CREATE
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.widget.v1.Messages.BulkCreateMessages
 */
export async function bulkCreateMessages(
  options?: NonNullablePaths<
    BulkCreateMessagesOptions,
    `messages.${number}.messageType`,
    4
  >
): Promise<
  NonNullablePaths<
    BulkCreateMessagesResponse,
    `messages` | `messages.${number}.sender` | `messages.${number}.messageType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    messages: options?.messages,
  });

  const reqOpts =
    ambassadorWixInnovationWidgetV1Message.bulkCreateMessages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { messages: '$[0].messages' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateMessagesOptions {
  /**
   * Messages to be created.
   * @minSize 1
   * @maxSize 25
   */
  messages?: Message[];
}

/**
 * Creates a single message and streams the assistant's response back.
 *
 * First frame: CreateMessageStreamResponse.response.question - the persisted input Message with server-assigned id and timestamps.
 * Intermediate frames: CreateMessageStreamResponse.response.message - text chunks of the assistant's answer as they are generated.
 * Final frame before completion: CreateMessageStreamResponse.response.answer - the persisted answer Message.
 * @param message - Message to create and stream a response for.
 * @internal
 * @documentationMaturity preview
 * @requiredField message
 * @permissionId innovation:widget:v1:message:wix-ai-site-chat:create_message_stream
 * @fqn wix.innovation.assistant.widget.v1.Messages.CreateMessageStream
 */
export async function createMessageStream(
  message: Message
): Promise<
  NonNullablePaths<
    CreateMessageStreamResponse,
    | `question.sender`
    | `question.messageType`
    | `question.body.additionalData`
    | `question.visitorOptions`
    | `question.assistantActions`
    | `question.visitorOptionsFields`
    | `answer.sender`
    | `answer.messageType`
    | `answer.body.additionalData`
    | `answer.visitorOptions`
    | `answer.assistantActions`
    | `answer.visitorOptionsFields`
    | `message`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ message: message });

  const reqOpts =
    ambassadorWixInnovationWidgetV1Message.createMessageStream(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { message: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['message']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of messages.
 *
 * Up to 100 Messages can be returned per request.
 *
 * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is retrieving the messages.
 * @public
 * @param options - Additional options for listing messages.
 * @permissionId INNOVATION_LAB.CHAT_WIDGET_MESSAGE_READ
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.widget.v1.Messages.ListMessages
 */
export async function listMessages(
  options?: ListMessagesOptions
): Promise<
  NonNullablePaths<
    ListMessagesResponse,
    `messages` | `messages.${number}.sender` | `messages.${number}.messageType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    cursorPaging: options?.cursorPaging,
  });

  const reqOpts = ambassadorWixInnovationWidgetV1Message.listMessages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { cursorPaging: '$[0].cursorPaging' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMessagesOptions {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

/**
 * Allows site visitor to create upload url to use for uploading a file.
 * @param mimeType - File type e.g. "image/png".
 * @public
 * @documentationMaturity preview
 * @requiredField mimeType
 * @permissionId INNOVATION_LAB.CHAT_WIDGET_MESSAGE_CREATE
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.widget.v1.Messages.MediaAttachmentUploadUrl
 */
export async function mediaAttachmentUploadUrl(
  mimeType: string,
  options?: MediaAttachmentUploadUrlOptions
): Promise<NonNullablePaths<MediaAttachmentUploadUrlResponse, `uploadUrl`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    mimeType: mimeType,
    fileName: options?.fileName,
    parentFolderId: options?.parentFolderId,
    filePath: options?.filePath,
  });

  const reqOpts =
    ambassadorWixInnovationWidgetV1Message.mediaAttachmentUploadUrl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          mimeType: '$[0]',
          fileName: '$[1].fileName',
          parentFolderId: '$[1].parentFolderId',
          filePath: '$[1].filePath',
        },
        singleArgumentUnchanged: false,
      },
      ['mimeType', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MediaAttachmentUploadUrlOptions {
  /**
   * File name
   * @maxLength 100
   */
  fileName?: string | null;
  /**
   * @maxLength 100
   * @deprecated
   * @targetRemovalDate 2026-06-25
   */
  parentFolderId?: string | null;
  /**
   * @maxLength 100
   * @deprecated
   * @targetRemovalDate 2026-06-25
   */
  filePath?: string | null;
}

/**
 * Retrieves messages by inbox conversation ID.
 *
 * Up to 100 Messages can be returned per request.
 * @param inboxConversationId - conversationId of the conversation in inbox.
 *
 * Can be found in [Message.conversationId](https://dev.wix.com/docs/api-reference/crm/communication/inbox/messages/message-object)
 * @public
 * @documentationMaturity preview
 * @requiredField inboxConversationId
 * @permissionId INNOVATION_LAB.CHAT_WIDGET_MESSAGE_BY_INBOX_READ
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.widget.v1.Messages.BulkGetByInboxMessages
 */
export async function bulkGetByInboxMessages(
  inboxConversationId: string,
  options?: BulkGetByInboxMessagesOptions
): Promise<
  NonNullablePaths<
    BulkGetByInboxMessagesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.message.sender`
    | `results.${number}.message.messageType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inboxConversationId: inboxConversationId,
    inboxMessageSequences: options?.inboxMessageSequences,
  });

  const reqOpts =
    ambassadorWixInnovationWidgetV1Message.bulkGetByInboxMessages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          inboxConversationId: '$[0]',
          inboxMessageSequences: '$[1].inboxMessageSequences',
        },
        singleArgumentUnchanged: false,
      },
      ['inboxConversationId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkGetByInboxMessagesOptions {
  /**
   * List of message sequences in the inbox conversation to retrieve messages for.
   *
   * Can be found in [Message.sequence](https://dev.wix.com/docs/api-reference/crm/communication/inbox/messages/message-object)
   * @maxLength 20
   * @minSize 1
   * @maxSize 100
   */
  inboxMessageSequences?: string[] | null;
}
